import { computed, onMounted, ref } from 'vue'
import { fetchDistributeFeed, type DistributeFeedItem } from '@/api/distribute'
import { DISTRIBUTE_FEED_EMPTY_PAGE_AUTOFILL_LIMIT } from '@/constants/distribute'
import { getClientUniqueId } from '@/utils/consts'

export function useMessageEmptyRecommendations() {
  const items = ref<DistributeFeedItem[]>([])
  const pageNo = ref(0)
  const hasNext = ref(true)
  const loadingInitial = ref(false)
  const loadingMore = ref(false)
  const initialized = ref(false)
  const error = ref<Error | null>(null)

  const loading = computed(() => loadingInitial.value || loadingMore.value)

  function reset() {
    items.value = []
    pageNo.value = 0
    hasNext.value = true
    loadingInitial.value = false
    loadingMore.value = false
    initialized.value = false
    error.value = null
  }

  async function fetchNext() {
    if (loading.value) {
      return
    }

    if (!hasNext.value && initialized.value) {
      return
    }

    let nextPageNo = pageNo.value + 1
    const isFirstPage = nextPageNo === 1
    let emptyPageCount = 0

    error.value = null
    loadingInitial.value = isFirstPage
    loadingMore.value = !isFirstPage

    try {
      while (true) {
        const response = await fetchDistributeFeed({
          pageNo: nextPageNo,
          // Message-page recommendations should mirror the guest discover feed batch for now.
          clientUniqueId: getClientUniqueId(),
        })

        const page = response.data

        if (!page) {
          hasNext.value = false
          initialized.value = true
          return
        }

        const existingIds = new Set(items.value.map((item) => item.id))
        const nextItems = page.list.filter((item) => !existingIds.has(item.id))

        if (nextItems.length) {
          items.value = items.value.concat(nextItems)
        }

        pageNo.value = nextPageNo
        hasNext.value = page.hasNext
        initialized.value = true

        if (nextItems.length || !page.hasNext) {
          return
        }

        emptyPageCount += 1

        if (emptyPageCount >= DISTRIBUTE_FEED_EMPTY_PAGE_AUTOFILL_LIMIT) {
          hasNext.value = false
          return
        }

        nextPageNo += 1
      }
    } catch (nextError) {
      error.value =
        nextError instanceof Error ? nextError : new Error('推荐内容加载失败，请稍后再试')
    } finally {
      loadingInitial.value = false
      loadingMore.value = false
    }
  }

  async function refresh() {
    reset()
    await fetchNext()
  }

  onMounted(() => {
    if (!initialized.value && !loading.value) {
      void fetchNext()
    }
  })

  return {
    items,
    hasNext,
    loadingInitial,
    loadingMore,
    error,
    refresh,
    fetchNext,
  }
}
